import React from 'react';
import { cn } from '@/lib/utils';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import type { RiqueMessage, RiqueBriefing } from '@/lib/riqueApi';
import { RiqueRichContent } from '@/components/Rique/RiqueRichContent';
import { RiqueMarkdown } from '@/components/Rique/RiqueMarkdown';

interface RiqueMessageListProps {
  messages: RiqueMessage[];
  streamingContent?: string;
  isLoading?: boolean;
  compact?: boolean;
  expanded?: boolean;
  briefing?: RiqueBriefing | null;
  /** Empty state: short briefing only (chips live in composer) */
  emptyMinimal?: boolean;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const RiqueMessageList: React.FC<RiqueMessageListProps> = ({
  messages,
  streamingContent,
  isLoading,
  compact = false,
  expanded = false,
  briefing,
  emptyMinimal = false,
}) => {
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, isLoading]);

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto', compact ? 'px-3 py-3' : 'px-4 py-4')}>
      {messages.length === 0 && !streamingContent && !isLoading ? (
        <div className={cn('flex flex-col items-center gap-2 py-5 text-center', expanded && 'py-8')}>
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white p-1 shadow-sm ring-1 ring-border/40">
            <RiqueIcon className="h-full w-full" />
          </div>
          <p className="text-sm font-medium text-foreground">Olá! Sou o Rique.Ia</p>
          <p className={cn('max-w-[17rem] text-xs leading-relaxed text-muted-foreground', expanded && 'max-w-md')}>
            {emptyMinimal
              ? 'Escolha uma pergunta abaixo ou digite livremente.'
              : 'Analista do ecossistema PortalFly e Zeh Motoca.'}
          </p>
          {!emptyMinimal ? (
            <ul className={cn('max-w-[18rem] space-y-1 text-left text-[11px] text-muted-foreground', expanded && 'max-w-md text-xs')}>
              <li>· Cobrança, aging e operadores</li>
              <li>· ERP, frota, faturamento e WhatsApp</li>
              <li>· Documentação do sistema e rascunhos de mensagem</li>
            </ul>
          ) : null}
          {briefing && briefing.sections.length > 0 ? (
            <div className={cn('mt-2 w-full max-w-[18rem] rounded-lg border border-border/70 bg-muted/30 p-2 text-left', expanded && 'max-w-md p-3')}>
              <p className="text-[10px] font-semibold uppercase text-muted-foreground">Briefing</p>
              {briefing.sections.slice(0, emptyMinimal ? 1 : undefined).map((section) => (
                <div key={section.titulo} className="mt-1.5">
                  <p className="text-[10px] font-medium text-foreground">{section.titulo}</p>
                  <ul className="mt-0.5 space-y-0.5 text-[10px] text-muted-foreground">
                    {section.itens.slice(0, emptyMinimal ? 3 : undefined).map((item) => (
                      <li key={item}>· {item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={cn('flex flex-col gap-0.5', msg.role === 'USER' ? 'items-end' : 'items-start')}
        >
          <div
            className={cn(
              'text-sm',
              expanded ? 'max-w-[85%]' : 'max-w-[88%]',
              compact ? 'px-2.5 py-2' : 'px-3 py-2',
              msg.role === 'USER'
                ? 'whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary text-primary-foreground'
                : 'rounded-2xl rounded-bl-md border border-border/80 bg-muted/50 text-foreground'
            )}
          >
            {msg.role === 'ASSISTANT' ? <RiqueMarkdown content={msg.content} /> : msg.content}
          </div>
          {msg.role === 'ASSISTANT' && msg.metadata?.faqFastPath ? (
            <span className="px-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">Resposta rápida</span>
          ) : null}
          {msg.role === 'ASSISTANT' && msg.metadata ? (
            <RiqueRichContent
              compact={compact}
              expanded={expanded}
              visualizations={msg.metadata.visualizations}
              deepLinks={msg.metadata.deepLinks}
              actionDraft={msg.metadata.actionDraft}
            />
          ) : null}
          {msg.createdAt && !msg.id.startsWith('temp-') ? (
            <span className="px-1 text-[10px] text-muted-foreground/80">{formatTime(msg.createdAt)}</span>
          ) : null}
        </div>
      ))}
      {streamingContent ? (
        <div className="flex flex-col items-start gap-0.5">
          <div
            className={cn(
              'max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-bl-md border border-border/80 bg-muted/50 text-sm text-foreground',
              compact ? 'px-2.5 py-2' : 'px-3 py-2'
            )}
          >
            {streamingContent}
            <span className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse bg-foreground/70" aria-hidden />
          </div>
        </div>
      ) : null}
      {isLoading && !streamingContent ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-flex gap-0.5">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:0ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:150ms]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:300ms]" />
          </span>
          Consultando dados…
        </div>
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
};
